import { useIntl } from 'react-intl';
import InlineText from '../../InlineEdit/InlineText';
import menuSectionMessages from '../MenuSection/messages';
import messages from './messages';
import { imageScaleUrl } from '../../../helpers/imageScales';
import './setmenu.scss';

interface Choice {
  '@id'?: string;
  title?: string;
}

interface Course {
  '@id'?: string;
  label?: string;
  choices?: Choice[];
}

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

const View = (props: any) => {
  const intl = useIntl();
  const { data, isEditMode, block, onChangeBlock, imageSlot } = props;
  const {
    eyebrow = '',
    heading = '',
    price = '',
    price_note = '',
    service_note = '',
    closing_note = '',
    show_allergen_notice = false,
  } = data;
  const courses: Course[] = Array.isArray(data.courses) ? data.courses : [];

  const update = (field: string, value: unknown) =>
    onChangeBlock(block, { ...data, [field]: value });

  const setCourses = (next: Course[]) => update('courses', next);

  const updateCourse = (index: number, patch: Partial<Course>) =>
    setCourses(
      courses.map((course, i) =>
        i === index ? { ...course, ...patch } : course,
      ),
    );

  const addCourse = () =>
    setCourses([...courses, { '@id': newId(), label: '', choices: [] }]);

  const removeCourse = (index: number) =>
    setCourses(courses.filter((_, i) => i !== index));

  const choicesOf = (course: Course) => course.choices ?? [];

  const updateChoice = (
    courseIndex: number,
    choiceIndex: number,
    title: string,
  ) =>
    updateCourse(courseIndex, {
      choices: choicesOf(courses[courseIndex]).map((choice, i) =>
        i === choiceIndex ? { ...choice, title } : choice,
      ),
    });

  const addChoice = (courseIndex: number) =>
    updateCourse(courseIndex, {
      choices: [
        ...choicesOf(courses[courseIndex]),
        { '@id': newId(), title: '' },
      ],
    });

  const removeChoice = (courseIndex: number, choiceIndex: number) =>
    updateCourse(courseIndex, {
      choices: choicesOf(courses[courseIndex]).filter(
        (_, i) => i !== choiceIndex,
      ),
    });

  // A text is editable in place in edit mode and plain text otherwise.
  const text = (
    value: string,
    onChange: (next: string) => void,
    placeholder: string,
  ) =>
    isEditMode ? (
      <InlineText
        value={value}
        onChange={onChange}
        renderTag="span"
        placeholder={placeholder}
      />
    ) : (
      value
    );

  // Empty texts are hidden on the public page but stay editable.
  const show = (value: string) => isEditMode || Boolean(value);

  const image = Array.isArray(data.image) ? data.image[0] : undefined;
  const imageUrl = image?.['@id'] ? imageScaleUrl(image, 'great') : null;
  const imageElement = imageUrl ? (
    <img src={imageUrl} alt={image?.title ?? ''} loading="lazy" />
  ) : null;
  const slotted = imageSlot?.({
    fieldId: 'image',
    value: data.image,
    element: imageElement,
  });
  const hasImage = Boolean(imageUrl);
  const showFigure = hasImage || Boolean(slotted);

  return (
    <section className={`set-menu${hasImage ? ' set-menu--with-image' : ''}`}>
      <div className="shell set-menu-inner">
        {showFigure && (
          <figure className={`set-menu-figure${hasImage ? '' : ' is-empty'}`}>
            {slotted ?? imageElement}
          </figure>
        )}
        <div className="set-menu-body">
          <header className="set-menu-head">
            {show(eyebrow) && (
              <p className="eyebrow">
                {text(
                  eyebrow,
                  (v) => update('eyebrow', v),
                  intl.formatMessage(messages.eyebrowPlaceholder),
                )}
              </p>
            )}
            {show(heading) && (
              <h2>
                {text(
                  heading,
                  (v) => update('heading', v),
                  intl.formatMessage(messages.headingPlaceholder),
                )}
              </h2>
            )}
            {show(price) && (
              <p className="set-menu-price">
                <span className="set-menu-amount">
                  {text(
                    price,
                    (v) => update('price', v),
                    intl.formatMessage(messages.pricePlaceholder),
                  )}
                </span>
                {show(price_note) && (
                  <span className="set-menu-price-note">
                    {text(
                      price_note,
                      (v) => update('price_note', v),
                      intl.formatMessage(messages.priceNotePlaceholder),
                    )}
                  </span>
                )}
              </p>
            )}
            {show(service_note) && (
              <p className="set-menu-service">
                {text(
                  service_note,
                  (v) => update('service_note', v),
                  intl.formatMessage(messages.serviceNotePlaceholder),
                )}
              </p>
            )}
          </header>

          {courses.length > 0 ? (
            <ol className="set-menu-courses">
              {courses.map((course, ci) => {
                const choices = choicesOf(course);
                return (
                  <li
                    className="set-menu-course inline-host"
                    key={course['@id'] ?? ci}
                  >
                    <p className="set-menu-course-label">
                      <span>
                        {text(
                          course.label ?? '',
                          (v) => updateCourse(ci, { label: v }),
                          intl.formatMessage(messages.courseLabelPlaceholder),
                        )}
                      </span>
                    </p>
                    {isEditMode && (
                      <button
                        type="button"
                        className="inline-remove set-menu-remove-course"
                        aria-label={intl.formatMessage(messages.removeCourse, {
                          label:
                            course.label ||
                            intl.formatMessage(messages.removeCourseFallback),
                        })}
                        onClick={() => removeCourse(ci)}
                      >
                        ✕
                      </button>
                    )}
                    {choices.length > 0 && (
                      <ul className="set-menu-choices">
                        {choices.map((choice, chi) => (
                          <li
                            className="set-menu-choice inline-host"
                            key={choice['@id'] ?? chi}
                          >
                            {chi > 0 && (
                              <span className="set-menu-or">
                                {intl.formatMessage(messages.or)}
                              </span>
                            )}
                            <p className="set-menu-dish">
                              {text(
                                choice.title ?? '',
                                (v) => updateChoice(ci, chi, v),
                                intl.formatMessage(messages.choicePlaceholder),
                              )}
                            </p>
                            {isEditMode && (
                              <button
                                type="button"
                                className="inline-remove set-menu-remove-choice"
                                aria-label={intl.formatMessage(
                                  messages.removeChoice,
                                  {
                                    title:
                                      choice.title ||
                                      intl.formatMessage(
                                        messages.removeChoiceFallback,
                                      ),
                                  },
                                )}
                                onClick={() => removeChoice(ci, chi)}
                              >
                                ✕
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                    {isEditMode && (
                      <button
                        type="button"
                        className="inline-add set-menu-add-choice"
                        onClick={() => addChoice(ci)}
                      >
                        {intl.formatMessage(messages.addChoice)}
                      </button>
                    )}
                  </li>
                );
              })}
            </ol>
          ) : (
            isEditMode && (
              <p className="set-menu-empty">
                {intl.formatMessage(messages.emptyState)}
              </p>
            )
          )}

          {isEditMode && (
            <button
              type="button"
              className="inline-add inline-add--full set-menu-add-course"
              onClick={addCourse}
            >
              {intl.formatMessage(messages.addCourse)}
            </button>
          )}

          {show(closing_note) && (
            <p className="set-menu-closing">
              {text(
                closing_note,
                (v) => update('closing_note', v),
                intl.formatMessage(messages.closingNotePlaceholder),
              )}
            </p>
          )}

          {show_allergen_notice && (
            <p className="allergie">
              {intl.formatMessage(menuSectionMessages.allergenNotice)}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default View;
